// Thermal Printer Service for ESC/POS compatible printers
// Uses Web Serial API for direct USB/Serial communication
import { getTaxRate, splitTaxInclusive, taxLabel } from './systemFeatures';
import { receiptPaymentLabel, tenderTipTotal } from './paymentMethods';

class ThermalPrinterService {
    constructor() {
        this.port = null;
        this.writer = null;
        this.reader = null;
        this.connected = false;
        this.printerModel = null;

        // Load printer configuration from localStorage
        this.loadConfig();
    }

    // Load printer configuration
    loadConfig() {
        try {
            const savedConfig = localStorage.getItem('printerConfig');
            if (savedConfig) {
                const config = JSON.parse(savedConfig);
                this.lineWidth = config.width || 32;
                this.baudRate = config.baudRate || 9600;
                this.fontSize = config.fontSize || 'normal';
            } else {
                // Default configuration
                this.lineWidth = 32;
                this.baudRate = 9600;
                this.fontSize = 'normal';
            }
        } catch (error) {
            console.error('Error loading printer config:', error);
            this.lineWidth = 32;
            this.baudRate = 9600;
            this.fontSize = 'normal';
        }
    }

    // Save printer configuration
    saveConfig(width, baudRate, fontSize) {
        const config = { width, baudRate, fontSize };
        localStorage.setItem('printerConfig', JSON.stringify(config));
        this.lineWidth = width;
        this.baudRate = baudRate;
        this.fontSize = fontSize;
    }

    // ESC/POS Commands
    static ESC = 0x1B;
    static GS = 0x1D;
    static LF = 0x0A;
    static CR = 0x0D;
    static SPACE = 0x20;

    // Command sequences
    static COMMANDS = {
        // Initialize printer
        INIT: [0x1B, 0x40],
        
        // Text formatting
        BOLD_ON: [0x1B, 0x45, 0x01],
        BOLD_OFF: [0x1B, 0x45, 0x00],
        UNDERLINE_ON: [0x1B, 0x2D, 0x01],
        UNDERLINE_OFF: [0x1B, 0x2D, 0x00],
        DOUBLE_HEIGHT_ON: [0x1B, 0x21, 0x10],
        DOUBLE_HEIGHT_OFF: [0x1B, 0x21, 0x00],
        DOUBLE_WIDTH_ON: [0x1B, 0x21, 0x20],
        DOUBLE_WIDTH_OFF: [0x1B, 0x21, 0x00],
        
        // Alignment
        ALIGN_LEFT: [0x1B, 0x61, 0x00],
        ALIGN_CENTER: [0x1B, 0x61, 0x01],
        ALIGN_RIGHT: [0x1B, 0x61, 0x02],
        
        // Line feed
        LINE_FEED: [0x0A],
        DOUBLE_LINE_FEED: [0x0A, 0x0A],
        
        // Cut paper
        CUT_PAPER: [0x1D, 0x56, 0x00],
        PARTIAL_CUT: [0x1D, 0x56, 0x01],
        
        // Cash drawer
        OPEN_DRAWER: [0x1B, 0x70, 0x00, 0x19, 0xFA],
        
        // Character set (for Spanish characters)
        CHAR_SET_SPAIN: [0x1B, 0x52, 0x0C],
        CODE_PAGE_858: [0x1B, 0x74, 0x13] // CP858 - includes € symbol
    };

    // Check if Web Serial API is supported
    isSupported() {
        return 'serial' in navigator;
    }

    // Connect to thermal printer
    async connect() {
        if (!this.isSupported()) {
            throw new Error('Web Serial API no es compatible con este navegador. Use Chrome/Edge 89+.');
        }

        try {
            // Request port selection
            this.port = await navigator.serial.requestPort();
            
            // Open connection with configured baud rate
            await this.port.open({
                baudRate: this.baudRate,
                dataBits: 8,
                stopBits: 1,
                parity: 'none',
                flowControl: 'none'
            });

            // Get writer and reader streams
            this.writer = this.port.writable.getWriter();
            this.reader = this.port.readable.getReader();

            this.connected = true;
            
            // Initialize printer
            await this.sendCommand(ThermalPrinterService.COMMANDS.INIT);
            await this.sendCommand(ThermalPrinterService.COMMANDS.CHAR_SET_SPAIN);
            await this.sendCommand(ThermalPrinterService.COMMANDS.CODE_PAGE_858);
            
            console.log('Impresora térmica conectada exitosamente');
            return true;
            
        } catch (error) {
            console.error('Error conectando a impresora térmica:', error);
            throw new Error('No se pudo conectar a la impresora térmica: ' + error.message);
        }
    }

    // Disconnect from printer
    async disconnect() {
        if (this.writer) {
            this.writer.releaseLock();
            this.writer = null;
        }
        
        if (this.reader) {
            this.reader.releaseLock();
            this.reader = null;
        }
        
        if (this.port) {
            await this.port.close();
            this.port = null;
        }
        
        this.connected = false;
        console.log('Impresora térmica desconectada');
    }

    // Send raw command to printer
    async sendCommand(command) {
        if (!this.connected || !this.writer) {
            throw new Error('Impresora no conectada');
        }

        try {
            const uint8Array = new Uint8Array(command);
            await this.writer.write(uint8Array);
        } catch (error) {
            console.error('Error enviando comando:', error);
            throw error;
        }
    }

    // Send text to printer
    async sendText(text) {
        if (!this.connected || !this.writer) {
            throw new Error('Impresora no conectada');
        }

        try {
            const encoder = new TextEncoder();
            const uint8Array = encoder.encode(text);
            await this.writer.write(uint8Array);
        } catch (error) {
            console.error('Error enviando texto:', error);
            throw error;
        }
    }

    // Generate ESC/POS commands for receipt
    generateReceiptCommands(orderItems, orderId, tableName, orderName, specialName = null, closedAt = null, payments = []) {
        const commands = [];
        
        // Initialize
        commands.push(...ThermalPrinterService.COMMANDS.INIT);
        commands.push(...ThermalPrinterService.COMMANDS.CHAR_SET_SPAIN);
        commands.push(...ThermalPrinterService.COMMANDS.CODE_PAGE_858);
        
        // Header - centered and bold
        commands.push(...ThermalPrinterService.COMMANDS.ALIGN_CENTER);
        commands.push(...ThermalPrinterService.COMMANDS.BOLD_ON);
        const headerText = 'CERVECERIA GIBBONS\n';
        commands.push(...new TextEncoder().encode(headerText));
        
        commands.push(...ThermalPrinterService.COMMANDS.BOLD_OFF);
        const addressText = 'C. ESCORZA 848\nCGI181112777\n';
        commands.push(...new TextEncoder().encode(addressText));
        
        // Separator line (use configured width)
        const separator = '='.repeat(this.lineWidth) + '\n';
        commands.push(...new TextEncoder().encode(separator));
        
        // Order info - left aligned
        commands.push(...ThermalPrinterService.COMMANDS.ALIGN_LEFT);
        
        const now = closedAt ? new Date(closedAt) : new Date();
        const dateTime = now.toLocaleString('es-MX', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
        
        const orderInfo = `\nFecha/Hora: ${dateTime}\nOrden #: ${orderId}\nMesa: ${tableName || 'N/A'}\n`;
        commands.push(...new TextEncoder().encode(orderInfo));
        
        if (orderName && orderName !== tableName) {
            const nameInfo = `Nombre: ${orderName}\n`;
            commands.push(...new TextEncoder().encode(nameInfo));
        }

        // Special/promotion section (if applied)
        if (specialName) {
            commands.push(...ThermalPrinterService.COMMANDS.ALIGN_CENTER);
            // Bold on
            commands.push(...[0x1B, 0x45, 0x01]);
            commands.push(...[0x1B, 0x21, 0x08]);
            const specialHeader = '\n--- PROMOCION APLICADA ---\n';
            commands.push(...new TextEncoder().encode(specialHeader));
            const specialText = `${specialName}\n`;
            commands.push(...new TextEncoder().encode(specialText));
            // Bold off
            commands.push(...[0x1B, 0x45, 0x00]);
            commands.push(...[0x1B, 0x21, 0x00]);
            commands.push(...ThermalPrinterService.COMMANDS.ALIGN_LEFT);
        }

        // Items header (use configured width)
        const dashLine = '-'.repeat(this.lineWidth) + '\n';
        const itemsHeader = '\n' + dashLine + 'CANT PRODUCTO             PRECIO\n' + dashLine;
        commands.push(...new TextEncoder().encode(itemsHeader));
        
        // Calculate totals
        let subtotal = 0;
        let totalDiscount = 0;

        // Items (adapt to configured width)
        orderItems.forEach(item => {
            const qty = item.quantity;
            const name = item.product_name;
            const price = Number(item.price || 0);
            const discount = Number(item.discount || 0);
            const itemTotal = qty * (price - discount);
            subtotal += itemTotal;

            const qtyStr = qty.toString().padStart(2);
            // Adjust max name length based on line width
            const maxNameLength = this.lineWidth - 14; // Reserve space for qty and price
            const truncatedName = name.length > maxNameLength
                ? name.substring(0, maxNameLength - 3) + '...'
                : name;
            const priceStr = `$${itemTotal.toFixed(2)}`.padStart(8);
            const itemLine = `${qtyStr}x ${truncatedName.padEnd(maxNameLength)}${priceStr}\n`;

            commands.push(...new TextEncoder().encode(itemLine));

            if (discount > 0) {
                // Display total discount for the line (per-unit discount * quantity)
                const lineDiscount = discount * qty;
                totalDiscount += lineDiscount;
                const discountLine = `    (Descuento: -$${lineDiscount.toFixed(2)})\n`;
                commands.push(...new TextEncoder().encode(discountLine));
            }
        });
        
        // Totals (adapt to configured width)
        // Note: IVA is already included in prices, so calculate backwards
        const total = subtotal;
        const ivaLabel = taxLabel(getTaxRate());
        const { subtotal: subtotalWithoutIva, tax: ivaAmount } = splitTaxInclusive(total);

        const discountPadding = this.lineWidth - 'Descuento:'.length - ` -$${totalDiscount.toFixed(2)}`.length;
        const subtotalPadding = this.lineWidth - 'Subtotal:'.length - ` $${subtotalWithoutIva.toFixed(2)}`.length;
        const ivaPadding = this.lineWidth - `${ivaLabel}:`.length - ` $${ivaAmount.toFixed(2)}`.length;
        const totalPadding = this.lineWidth - 'TOTAL:'.length - ` $${total.toFixed(2)}`.length;

        let totalsText = dashLine;
        // Show total discount if there is any
        if (totalDiscount > 0) {
            // Bold on for discount line
            commands.push(...[0x1B, 0x45, 0x01]);
            commands.push(...[0x1B, 0x21, 0x08]);
            const discountText = `Descuento:${' '.repeat(Math.max(1, discountPadding))}-$${totalDiscount.toFixed(2)}\n`;
            commands.push(...new TextEncoder().encode(discountText));
            // Bold off
            commands.push(...[0x1B, 0x45, 0x00]);
            commands.push(...[0x1B, 0x21, 0x00]);
            // Add blank line after discount
            commands.push(...new TextEncoder().encode('\n'));
        }
        totalsText = `Subtotal:${' '.repeat(Math.max(1, subtotalPadding))}$${subtotalWithoutIva.toFixed(2)}\n` +
            `${ivaLabel}:${' '.repeat(Math.max(1, ivaPadding))}$${ivaAmount.toFixed(2)}\n`;
        commands.push(...new TextEncoder().encode(totalsText));

        // TOTAL line in bold - use both emphasis methods for maximum compatibility
        // ESC E (bold emphasis)
        commands.push(...[0x1B, 0x45, 0x01]);
        // ESC ! with bold bit set (double strike)
        commands.push(...[0x1B, 0x21, 0x08]);
        const totalText = `TOTAL:${' '.repeat(Math.max(1, totalPadding))}$${total.toFixed(2)}\n`;
        commands.push(...new TextEncoder().encode(totalText));
        // Turn off both
        commands.push(...[0x1B, 0x45, 0x00]);
        commands.push(...[0x1B, 0x21, 0x00]);

        // How the check was paid, one line per tender
        if (payments.length > 0) {
            let paymentsText = dashLine + 'FORMA DE PAGO\n';
            payments.forEach(payment => {
                const label = `${receiptPaymentLabel(payment.payment_type_id)}:`;
                const amount = `$${Number(payment.amount).toFixed(2)}`;
                const padding = this.lineWidth - label.length - amount.length;
                paymentsText += `${label}${' '.repeat(Math.max(1, padding))}${amount}\n`;
            });
            const tipTotal = tenderTipTotal(payments);
            if (tipTotal > 0) {
                const tipText = `$${tipTotal.toFixed(2)}`;
                const tipPadding = this.lineWidth - 'Propina:'.length - tipText.length;
                paymentsText += `Propina:${' '.repeat(Math.max(1, tipPadding))}${tipText}\n`;
            }
            commands.push(...new TextEncoder().encode(paymentsText));
        }

        // Footer - centered
        commands.push(...ThermalPrinterService.COMMANDS.ALIGN_CENTER);
        const footerSeparator = '='.repeat(this.lineWidth) + '\n';
        commands.push(...new TextEncoder().encode(footerSeparator));
        
        const footerText = '\nAhorremos el agua,\nTomemos cerveza\n\n¡Gracias!\n';
        commands.push(...new TextEncoder().encode(footerText));

        const finalSeparator = '='.repeat(this.lineWidth) + '\n';
        commands.push(...new TextEncoder().encode(finalSeparator));
        
        // Feed and cut
        commands.push(...ThermalPrinterService.COMMANDS.LINE_FEED);
        commands.push(...ThermalPrinterService.COMMANDS.LINE_FEED);
        commands.push(...ThermalPrinterService.COMMANDS.CUT_PAPER);
        
        return new Uint8Array(commands);
    }

    // Generate ESC/POS commands for an item ticket (kitchen, bar, etc.)
    generateItemTicketCommands(items, orderName, tableName, areaLabel, userName = '') {
        const commands = [];

        // Initialize
        commands.push(...ThermalPrinterService.COMMANDS.INIT);
        commands.push(...ThermalPrinterService.COMMANDS.CHAR_SET_SPAIN);
        commands.push(...ThermalPrinterService.COMMANDS.CODE_PAGE_858);

        // Header - centered, bold, double height
        commands.push(...ThermalPrinterService.COMMANDS.ALIGN_CENTER);
        commands.push(...ThermalPrinterService.COMMANDS.BOLD_ON);
        commands.push(...ThermalPrinterService.COMMANDS.DOUBLE_HEIGHT_ON);
        commands.push(...new TextEncoder().encode(`${areaLabel}\n`));
        commands.push(...ThermalPrinterService.COMMANDS.DOUBLE_HEIGHT_OFF);
        commands.push(...ThermalPrinterService.COMMANDS.BOLD_OFF);

        // Separator
        const separator = '='.repeat(this.lineWidth) + '\n';
        commands.push(...new TextEncoder().encode(separator));

        // Order info - left aligned
        commands.push(...ThermalPrinterService.COMMANDS.ALIGN_LEFT);
        const now = new Date();
        const time = now.toLocaleString('es-MX', {
            hour: '2-digit', minute: '2-digit', hour12: false
        });
        let info = `${time}`;
        if (tableName) info += `  Mesa: ${tableName}`;
        if (orderName && orderName !== tableName) info += `  ${orderName}`;
        commands.push(...new TextEncoder().encode(info + '\n'));
        if (userName) {
            commands.push(...new TextEncoder().encode(`Server: ${userName}\n`));
        }

        const dashLine = '-'.repeat(this.lineWidth) + '\n';
        commands.push(...new TextEncoder().encode(dashLine));

        // Items - double height for readability
        commands.push(...ThermalPrinterService.COMMANDS.DOUBLE_HEIGHT_ON);
        items.forEach(item => {
            const qty = item.quantity || 1;
            const name = item.name;
            const maxLen = Math.floor(this.lineWidth / 2) - 4; // half width in double-height
            const truncated = name.length > maxLen ? name.substring(0, maxLen - 2) + '..' : name;
            let line = `${qty}x ${truncated}`;
            if (item.dontMake) line += ' [NO PREP]';
            commands.push(...new TextEncoder().encode(line + '\n'));
            if (item.note) {
                commands.push(...ThermalPrinterService.COMMANDS.DOUBLE_HEIGHT_OFF);
                commands.push(...new TextEncoder().encode(`   > ${item.note}\n`));
                commands.push(...ThermalPrinterService.COMMANDS.DOUBLE_HEIGHT_ON);
            }
        });
        commands.push(...ThermalPrinterService.COMMANDS.DOUBLE_HEIGHT_OFF);

        // Footer separator
        commands.push(...new TextEncoder().encode(dashLine));

        // Feed and cut
        commands.push(...ThermalPrinterService.COMMANDS.LINE_FEED);
        commands.push(...ThermalPrinterService.COMMANDS.LINE_FEED);
        commands.push(...ThermalPrinterService.COMMANDS.PARTIAL_CUT);

        return new Uint8Array(commands);
    }

    // Print item ticket (kitchen, bar, etc.)
    async printItemTicket(items, orderName, tableName, areaLabel, userName = '') {
        if (!this.connected) {
            throw new Error('Impresora térmica no conectada');
        }
        try {
            const commands = this.generateItemTicketCommands(items, orderName, tableName, areaLabel, userName);
            await this.writer.write(commands);
            console.log(`Item ticket printed: ${areaLabel}`);
            return true;
        } catch (error) {
            console.error('Error printing item ticket:', error);
            throw error;
        }
    }

    // Print receipt using thermal printer
    async printReceipt(orderItems, orderId, tableName, orderName, specialName = null, closedAt = null, payments = []) {
        if (!this.connected) {
            throw new Error('Impresora térmica no conectada');
        }

        try {
            const commands = this.generateReceiptCommands(orderItems, orderId, tableName, orderName, specialName, closedAt, payments);
            await this.writer.write(commands);
            console.log('Recibo impreso exitosamente en impresora térmica');
            return true;
        } catch (error) {
            console.error('Error imprimiendo recibo:', error);
            throw error;
        }
    }

    // Open cash drawer (if connected)
    async openCashDrawer() {
        if (!this.connected) {
            throw new Error('Impresora térmica no conectada');
        }

        try {
            await this.sendCommand(ThermalPrinterService.COMMANDS.OPEN_DRAWER);
            console.log('Cajón abierto');
            return true;
        } catch (error) {
            console.error('Error abriendo cajón:', error);
            throw error;
        }
    }

    // Test print function
    async testPrint() {
        if (!this.connected) {
            throw new Error('Impresora térmica no conectada');
        }

        try {
            await this.sendCommand(ThermalPrinterService.COMMANDS.INIT);
            await this.sendCommand(ThermalPrinterService.COMMANDS.ALIGN_CENTER);
            await this.sendCommand(ThermalPrinterService.COMMANDS.BOLD_ON);
            await this.sendText('PRUEBA DE IMPRESORA\n');
            await this.sendCommand(ThermalPrinterService.COMMANDS.BOLD_OFF);
            await this.sendText('Cerveceria Gibbons\n');
            await this.sendText('Impresión exitosa\n');
            await this.sendCommand(ThermalPrinterService.COMMANDS.LINE_FEED);
            await this.sendCommand(ThermalPrinterService.COMMANDS.LINE_FEED);
            await this.sendCommand(ThermalPrinterService.COMMANDS.CUT_PAPER);
            
            console.log('Prueba de impresión completada');
            return true;
        } catch (error) {
            console.error('Error en prueba de impresión:', error);
            throw error;
        }
    }

    // Get connection status
    isConnected() {
        return this.connected;
    }
}

// Create and export singleton instance
const thermalPrinterService = new ThermalPrinterService();
export default thermalPrinterService;